import React from 'react';
import { Box, Typography, makeStyles, Theme, Divider } from '@material-ui/core';
import { useScope } from '@/hooks/useScope';
import get from 'lodash/get';

interface RoomEntry {
  roomId: number;
  entityId: number;
  setAt: string;
  room: {
    id: number;
    name: string;
  } | null;
}

interface ProcessedLogEntry {
  location: string;
  enteredAt: string;
  exitedAt: string | null;
  isUnknownLocation: boolean;
  durationInMinutes: number | null;
}

interface StaffLocationHistoryProps {
  entries: RoomEntry[];
}

const useStyles = makeStyles((theme: Theme) => ({
  collectionList: {
    display: 'flex',
    gap: theme.spacing(1),
    flexDirection: 'column',
  },
  item: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2, 0),
  },
  info: {
    flex: 1,
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
    },
  },
  duration: {
    color: theme.palette.text.secondary,
  },
}));

const StaffLocationHistory: React.FC<StaffLocationHistoryProps> = ({ entries }) => {
  const classes = useStyles();
  const scope = useScope();
  const hospitalTimezone = get(scope, 'hospital.timezone.id');

  const calculateDuration = (startTime: string, endTime: string | null): number | null => {
    if (!endTime) return null;
    const start = new Date(startTime);
    const end = new Date(endTime);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
  };

  const formatDuration = (minutes: number | null): string => {
    if (minutes === null) return '-';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) return `${remainingMinutes}m`;
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatDateTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    const localDateTime = date.toLocaleString('en-US', {
      timeZone: hospitalTimezone || 'UTC',
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
    return localDateTime;
  };

  const processEntries = (entries: RoomEntry[]): ProcessedLogEntry[] => {
    const sortedEntries = [...entries].sort((a, b) => {
      const dateA = new Date(a.setAt);
      const dateB = new Date(b.setAt);
      return dateA.getTime() - dateB.getTime();
    });

    return sortedEntries.map((entry, index) => {
      const exitedAt = index < sortedEntries.length - 1 ? sortedEntries[index + 1].setAt : null;
      return {
        location: entry.room?.name ?? 'Unknown',
        enteredAt: entry.setAt,
        exitedAt,
        isUnknownLocation: !entry.room?.name,
        durationInMinutes: calculateDuration(entry.setAt, exitedAt),
      };
    });
  };

  const processedEntries = processEntries(entries);

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h3">Location History</Typography>
      </Box>
      <Box className={classes.collectionList}>
        {processedEntries.map((entry, index) => (
          <React.Fragment key={index}>
            <Box className={classes.item}>
              <Box className={classes.info}>
                <Typography variant="body2" color="textSecondary">
                  {formatDateTime(entry.enteredAt)}
                </Typography>
                <Typography variant="body2">{entry.location}</Typography>
                <Typography variant="body2" className={classes.duration}>
                  {entry.exitedAt
                    ? formatDuration(entry.durationInMinutes)
                    : entry.isUnknownLocation
                      ? 'Left Site'
                      : 'Current Location'}
                </Typography>
              </Box>
            </Box>
            <Divider />
          </React.Fragment>
        ))}
        {processedEntries.length === 0 && (
          <Typography color="textSecondary">No location history found for this day</Typography>
        )}
      </Box>
    </Box>
  );
};

export default StaffLocationHistory;
